import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Braces, Cable, Check, ChevronDown, Filter, KeyRound, Search } from 'lucide-react';
import { fetchIntegrations, getCachedIntegrations, type IntegrationListItem } from '../lib/catalog';
const PAGE_SIZE = 24;
const METHOD_OPTIONS = ['API', 'MCP', 'OAuth'] as const;
type IntegrationMethod = typeof METHOD_OPTIONS[number];
const METHOD_ICONS = {
  API: Braces,
  MCP: Cable,
  OAuth: KeyRound
} as const;
function getIntegrationMethods(integration: IntegrationListItem): Set<IntegrationMethod> {
  const methods = new Set<IntegrationMethod>();
  const row = integration as IntegrationListItem & Record<string, unknown>;
  const fields = ['method', 'methods', 'recommended_method', 'recommendedMethod', 'api', 'mcp', 'oauth', 'auth_type', 'auth_types', 'authType', 'authTypes', 'authentication', 'capabilities'];
  const addValue = (value: unknown, hint?: string) => {
    if (hint && value !== false && value != null && ['api', 'mcp', 'oauth', 'oauth2', 'rest', 'graphql'].includes(hint.toLowerCase())) {
      addValue(hint);
    }
    if (typeof value === 'string') {
      const tokens = value.toLowerCase().split(/[^a-z0-9]+/);
      if (tokens.includes('api') || tokens.includes('rest') || tokens.includes('graphql')) methods.add('API');
      if (tokens.includes('mcp')) methods.add('MCP');
      if (tokens.includes('oauth') || tokens.includes('oauth2')) methods.add('OAuth');
    } else if (Array.isArray(value)) {
      value.forEach(item => addValue(item));
    } else if (value && typeof value === 'object') {
      Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
        if (['key', 'name', 'type', 'method', 'auth_type', 'authType'].includes(key)) addValue(item);else if (['api', 'mcp', 'oauth', 'oauth2', 'rest', 'graphql'].includes(key.toLowerCase())) addValue(item, key);
      });
    }
  };
  fields.forEach(field => addValue(row[field], field));
  return methods;
}

// Fallback avatar (first letter) when an integration has no logo_url.
function LogoOrInitial({
  item
}: {
  item: IntegrationListItem;
}) {
  const [broken, setBroken] = useState(false);
  if (item.logo_url && !broken) {
    return <img src={item.logo_url} alt="" loading="lazy" onError={() => setBroken(true)} className="w-8 h-8 object-contain rounded flex-none" data-id="element-9644" data-mp="3lzsj67ooh2yg" />;
  }
  return <div className="w-8 h-8 rounded-lg bg-brand/10 text-brand-dark flex items-center justify-center text-sm font-bold flex-none" data-id="element-9645" data-mp="3q4a07z3nl25j">
      {item.display_name.charAt(0).toUpperCase()}
    </div>;
}
export function Integrations() {
  // Seed from the build-time prime cache when present (SSR/prerender), so the
  // static HTML renders the full list. In the browser the cache is empty →
  // falls through to the fetch below (unchanged behaviour).
  const primed = getCachedIntegrations();
  const [integrations, setIntegrations] = useState<IntegrationListItem[]>(primed ?? []);
  const [loading, setLoading] = useState(!primed);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedMethods, setSelectedMethods] = useState<IntegrationMethod[]>([]);
  const [methodsExpanded, setMethodsExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchIntegrations().then(rows => {
      if (!cancelled) {
        setIntegrations(rows);
        setError(null);
      }
    }).catch(e => {
      if (!cancelled) setError(e.message || 'Failed to load integrations');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    integrations.forEach(i => i.categories.forEach(c => counts.set(c.name, (counts.get(c.name) ?? 0) + 1)));
    return counts;
  }, [integrations]);
  const categories = useMemo(() => [...categoryCounts.keys()].sort(), [categoryCounts]);
  const filtered = useMemo(() => {
    return integrations.filter(i => {
      if (selectedCategory && !i.categories.some(c => c.name === selectedCategory)) return false;
      if (selectedMethods.length > 0) {
        const methods = getIntegrationMethods(i);
        if (methods.size > 0 && !selectedMethods.some(method => methods.has(method))) return false;
      }
      if (query) {
        const q = query.toLowerCase();
        return i.display_name.toLowerCase().includes(q) || i.slug.toLowerCase().includes(q) || i.categories.some(c => c.name.toLowerCase().includes(q));
      }
      return true;
    });
  }, [integrations, query, selectedCategory, selectedMethods]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const page = Math.min(currentPage, totalPages);
  const pageStart = (page - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(pageStart, pageStart + PAGE_SIZE);
  const rangeStart = filtered.length === 0 ? 0 : pageStart + 1;
  const rangeEnd = Math.min(pageStart + PAGE_SIZE, filtered.length);
  useEffect(() => {
    setCurrentPage(1);
  }, [query, selectedCategory, selectedMethods]);
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);
  const goToPage = (nextPage: number) => {
    setCurrentPage(Math.max(1, Math.min(nextPage, totalPages)));
  };
  const toggleMethod = (method: IntegrationMethod) => {
    setSelectedMethods(selected => selected.includes(method) ? selected.filter(item => item !== method) : [...selected, method]);
  };
  return <div className="min-h-screen bg-[#FAF8F4] pt-32 pb-24 px-6" data-id="element-9646" data-mp="1tksvti93w945">
      <div className="max-w-6xl mx-auto" data-id="element-9647" data-mp="1tmdn8z04jbxs">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-9648" data-mp="3rym74qx67mev">
          <h1 className="text-4xl md:text-5xl font-bold text-[#2B211A] mb-4 font-title tracking-tight" data-id="element-9649" data-mp="3nyc0rd5gutzh">
            All Integrations
          </h1>
          <p className="text-neutral-500 text-lg max-w-2xl mx-auto" data-id="element-9650" data-mp="1lav87r0a9ilf">
            Giga connects with the tools you already use. Search for an
            integration or browse by category below.
          </p>
        </div>

        {/* Search */}
        <div className="mb-8" data-id="element-9651" data-mp="1c95icpm1urpr">
          <div className="relative max-w-md mx-auto" data-id="element-9652" data-mp="1gc224jnbstva">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" data-id="element-9653" data-mp="1gahbv681nx38" />
            <input type="text" placeholder="Search integrations…" value={query} onChange={e => setQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 rounded-xl border border-neutral-200 bg-white text-[#2B211A] placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#EA580C]/30 focus:border-[#EA580C] transition-shadow" data-id="element-9654" data-mp="2401shigq6i9c" />
          </div>
        </div>

        {/* Results */}
        {loading ? <div data-loading="true" className="text-center py-20 text-neutral-400 text-lg" data-id="element-9655" data-mp="jonqr0wj3n44">
            Loading integrations…
          </div> : error ? <div className="text-center py-20" data-id="element-9656" data-mp="g3lzo6y4wxsd">
            <p className="text-neutral-500 text-lg" data-id="element-9657" data-mp="g6giju1i2ajo">
              Couldn't load integrations right now.
            </p>
            <p className="text-neutral-400 text-sm mt-2" data-id="element-9658" data-mp="q2jict8vpx3g">{error}</p>
          </div> : integrations.length === 0 ? <div className="text-center py-20" data-id="element-9659" data-mp="l3gng1mm5797">
            <p className="text-neutral-400 text-lg" data-id="element-9660" data-mp="l4rqu0c5cczs">No integrations available.</p>
          </div> : <div className="grid grid-cols-1 min-[960px]:grid-cols-4 gap-4 md:gap-6" data-id="element-9661" data-mp="3tkbg6o9p5b9k">
            <div className="min-[960px]:col-span-1 flex flex-col gap-2 min-[960px]:max-h-[600px] min-[960px]:overflow-y-auto min-[960px]:pr-2" data-id="element-9662" data-mp="3okyjlbt0wpts">
              <div className="rounded-lg border border-sand-200 text-[13px] text-ink" data-id="element-9663" data-mp="7508jnelkafz">
                <button type="button" onClick={() => setMethodsExpanded(expanded => !expanded)} aria-expanded={methodsExpanded} aria-controls="integration-method-filters" className="flex w-full items-center gap-2 rounded-lg py-2 px-3 text-left font-medium transition-colors hover:border-sand-300" data-id="element-9664" data-mp="2be6sq9z1qyy">
                  <span className="flex-1" data-id="element-9665" data-mp="2kbxidjoel0p8">Filters</span>
                  {selectedMethods.length > 0 && <span className="max-w-24 truncate text-xs font-semibold text-brand" data-id="element-9666" data-mp="2pa8tdxqfph91">{selectedMethods.join(', ')}</span>}
                  <ChevronDown className={`h-4 w-4 flex-none text-ink-faint transition-transform ${methodsExpanded ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-9667" data-mp="2u790mbz0sq1q" />
                </button>
                {methodsExpanded && <div id="integration-method-filters" className="space-y-2 border-t border-sand-200 px-3 py-2.5" data-id="element-9668" data-mp="3375tkpsg0b2l">
                  {METHOD_OPTIONS.map(method => {
                const MethodIcon = METHOD_ICONS[method];
                const iconColor = method === 'API' ? 'text-brand' : method === 'MCP' ? 'text-emerald-600' : 'text-amber-600';
                return <label key={method} className="group -mx-1.5 flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 font-medium transition-colors hover:bg-sand-100/70 focus-within:bg-sand-100/70" data-id="element-9669" data-mp="3my0six91ysj9">
                      <span className="relative flex h-4 w-4 flex-none items-center justify-center" data-id="element-9670" data-mp="1fcipvfof8s5v">
                        <input type="checkbox" checked={selectedMethods.includes(method)} onChange={() => toggleMethod(method)} className="peer h-4 w-4 cursor-pointer appearance-none rounded-[5px] border border-[#EAE5DD] bg-white shadow-[0_1px_2px_rgba(43,33,26,0.04)] transition-all hover:border-[#D9D1C7] checked:border-brand checked:bg-brand focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/25 focus-visible:ring-offset-1" data-id="element-9671" data-mp="1b8t9hdhwhgvx" />
                        <Check className="pointer-events-none absolute h-3 w-3 text-white opacity-0 transition-opacity peer-checked:opacity-100" strokeWidth={3} aria-hidden="true" data-id="element-9672" data-mp="1u2q2herqzgyn" />
                      </span>
                      <MethodIcon className={`h-4 w-4 flex-none ${iconColor}`} aria-hidden="true" data-id="element-9673" data-mp="2hpy6zuhcqlzm" />
                      <span data-id="element-9674" data-mp="2cz820icu8l5g">{method}</span>
                    </label>;
              })}
                </div>}
              </div>
              <button type="button" onClick={() => setSelectedCategory(null)} aria-pressed={!selectedCategory} className={`inline-flex items-center gap-2 rounded-lg border py-2 px-3 text-left text-[13px] transition-colors ${!selectedCategory ? 'border-brand text-brand font-semibold' : 'border-sand-200 text-ink font-medium hover:border-sand-300'}`} data-id="element-9675" data-mp="w8m6s6sllwov">
                <Filter className={`w-4 h-4 flex-none ${!selectedCategory ? '' : 'text-ink-faint'}`} data-id="element-9676" data-mp="11al6n6p4qbh2" />
                <span className="flex-1" data-id="element-9677" data-mp="1f5x2jhf0u7zk">All Categories</span>
                <span className="tabular-nums text-ink-faint" aria-label={`${integrations.length} integrations`} data-id="element-9678" data-mp="1a8f5aoad9h7e">{integrations.length}</span>
              </button>
              {categories.map(cat => <button key={cat} type="button" onClick={() => setSelectedCategory(cat)} aria-pressed={selectedCategory === cat} className={`inline-flex items-center gap-2 rounded-lg border py-2 px-3 text-left text-[13px] transition-colors ${selectedCategory === cat ? 'border-brand text-brand font-semibold' : 'border-sand-200 text-ink font-medium hover:border-sand-300'}`} data-id="element-9679" data-mp="1k01ao400wm28">
                  <span className={`w-1.5 h-1.5 rounded-full flex-none ${selectedCategory === cat ? 'bg-brand' : 'bg-sand-300'}`} data-id="element-9680" data-mp="2hl2vsr2di1l3" />
                  <span className="flex-1" data-id="element-9681" data-mp="23k05u389c74w">{cat}</span>
                  <span className="tabular-nums text-ink-faint" aria-label={`${categoryCounts.get(cat)} integrations`} data-id="element-9682" data-mp="28jv0mhv5lgv8">{categoryCounts.get(cat)}</span>
                </button>)}
            </div>
            <div className="min-[960px]:col-span-3 min-w-0" data-id="element-9683" data-mp="1ylowdtlvjyn7">
              <p className="text-sm text-neutral-500 mb-3" aria-live="polite" data-id="element-9684" data-mp="1yn9q5h0d7dk0">
                Showing {rangeStart}–{rangeEnd} of {filtered.length} integrations
              </p>
              {filtered.length === 0 ? <div className="text-center py-20" data-id="element-9685" data-mp="1ow5diny5w4sc">
                <p className="text-neutral-400 text-lg" data-id="element-9686" data-mp="1avkuprs8y8yu">No integrations found.</p>
                <button type="button" onClick={() => {
              setQuery('');
              setSelectedCategory(null);
              setSelectedMethods([]);
            }} className="mt-4 text-sm text-[#EA580C] hover:underline" data-id="element-9687" data-mp="1fv7hosxvn3ku">
                  Clear filters
                </button>
              </div> : <div className="grid grid-cols-1 sm:grid-cols-2 min-[960px]:grid-cols-3 gap-3 content-start" data-id="element-9688" data-mp="38s2sxaflwlqn">
              {pageItems.map(integration => <Link key={integration.slug} to={`/integrations/${integration.slug}`} className="group flex items-center gap-3 p-3 rounded-xl bg-white border border-sand-200 hover:border-sand-300 hover:shadow-sm transition-all min-w-0" data-id="element-9689" data-mp="33u19upi6kmf6">
                  <LogoOrInitial item={integration} data-id="element-9690" data-mp="a59afv7afad" />
                  <span className="min-w-0 flex flex-col text-left" data-id="element-9691" data-mp="3rhrieo0ov9ym">
                    <span className="text-[13px] font-semibold text-ink group-hover:text-brand transition-colors truncate" data-id="element-9692" data-mp="3mjg8yeeb31gx">
                      {integration.display_name}
                    </span>
                    {integration.categories[0] && <span className="text-[10px] text-ink-faint uppercase tracking-wider truncate" data-id="element-9693" data-mp="2b41af66g81jp">
                        {integration.categories[0].name}
                      </span>}
                  </span>
                </Link>)}
              </div>}
              {totalPages > 1 && <nav className="mt-6 flex items-center justify-center gap-3" aria-label="Integration results pagination" data-id="element-9694" data-mp="2aytpp8zkm53u">
                <button type="button" onClick={() => goToPage(page - 1)} disabled={page === 1} className="rounded-lg border border-sand-200 bg-white px-3 py-2 text-sm font-medium text-ink transition-colors hover:border-sand-300 disabled:cursor-not-allowed disabled:opacity-40" data-id="element-9695" data-mp="2fpieuhns2pzt">
                  Previous
                </button>
                <span className="text-sm text-neutral-500" aria-current="page" data-id="element-9696" data-mp="2tpktlv0sh3au">Page {page} of {totalPages}</span>
                <button type="button" onClick={() => goToPage(page + 1)} disabled={page === totalPages} className="rounded-lg border border-sand-200 bg-white px-3 py-2 text-sm font-medium text-ink transition-colors hover:border-sand-300 disabled:cursor-not-allowed disabled:opacity-40" data-id="element-9697" data-mp="2ym1u7op6da4t">
                  Next
                </button>
              </nav>}
            </div>
          </div>}
      </div>
    </div>;
}